import React, { useState, useEffect } from 'react';
import {
  FileText,
  CheckCircle,
  XCircle,
  Clock,
  Download,
  Hash,
  User,
  Calendar
} from 'lucide-react';
import { patientAPI } from '../services/api';

const ReferralVerification = () => {
  const [pendingReferrals, setPendingReferrals] = useState([]);
  const [selectedReferral, setSelectedReferral] = useState(null);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [notes, setNotes] = useState('');
  const [isRejecting, setIsRejecting] = useState(false);

  useEffect(() => {
    fetchPendingReferrals();
  }, []);

  const fetchPendingReferrals = async () => {
    try {
      setLoading(true);
      const response = await patientAPI.getPendingReferrals();
      setPendingReferrals(response.referrals);
    } catch (error) {
      console.error('Failed to fetch pending referrals:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewReferral = (referral, rejectMode = false) => {
    setSelectedReferral(referral);
    setNotes('');
    setIsRejecting(rejectMode);
  };

  const handleDownloadDocument = async (referralId) => {
    try {
      await patientAPI.downloadReferralDocument(referralId);
    } catch (error) {
      console.error('Failed to download document:', error);
      alert('Failed to download document. Please try again.');
    }
  };

  const handleVerifyOrReject = async () => {
    if (!selectedReferral) return;
    if (isRejecting && !notes.trim()) return alert('Please provide a rejection reason.');

    try {
      setVerifying(true);
      if (isRejecting) {
        await patientAPI.rejectReferral(selectedReferral.id, notes.trim(), notes.trim());
      } else {
        await patientAPI.verifyReferral(selectedReferral.id, notes.trim());
      }
      await fetchPendingReferrals();
      setSelectedReferral(null);
      setNotes('');
      alert(isRejecting ? 'Referral rejected successfully!' : 'Referral verified successfully!');
    } catch (error) {
      console.error('Failed to submit:', error);
      alert('Failed to process referral. Please try again.');
    } finally {
      setVerifying(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6 text-sm">
      <div className="mb-6">
        <h1 className="text-base font-bold text-gray-900 mb-1">Referral Verification Dashboard</h1>
        <p className="text-xs text-gray-600">Review and verify GP referrals from patients</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-yellow-600" />
            <div>
              <p className="text-xs font-semibold text-yellow-900">{pendingReferrals.length}</p>
              <p className="text-xs text-yellow-700">Pending Review</p>
            </div>
          </div>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <div className="flex items-center gap-2">
            <FileText className="w-4 h-4 text-blue-600" />
            <div>
              <p className="text-xs font-semibold text-blue-900">
                {pendingReferrals.filter(r => r.documentFileType === 'pdf').length}
              </p>
              <p className="text-xs text-blue-700">PDF Documents</p>
            </div>
          </div>
        </div>
        <div className="bg-green-50 border border-green-200 rounded-lg p-3">
          <div className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-green-600" />
            <div>
              <p className="text-xs font-semibold text-green-900">Ready</p>
              <p className="text-xs text-green-700">For Review</p>
            </div>
          </div>
        </div>
      </div>

      {pendingReferrals.length === 0 ? (
        <div className="text-center py-8 bg-gray-50 rounded-lg">
          <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
          <h3 className="text-base font-semibold text-gray-900 mb-1">All Caught Up!</h3>
          <p className="text-xs text-gray-600">No pending referrals to review at this time.</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="px-4 py-2 border-b border-gray-200">
            <h2 className="text-xs font-semibold text-gray-900">
              Pending Referrals ({pendingReferrals.length})
            </h2>
          </div>

          <div className="divide-y divide-gray-200">
            {pendingReferrals.map((referral) => (
              <div key={referral.id} className="p-4 hover:bg-gray-50 text-xs">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="text-sm font-semibold text-gray-900">
                      {referral.patient.name}
                    </h3>
                    <p className="text-[11px] text-gray-600">{referral.patient.email}</p>
                    <p className="text-[11px] text-gray-500 mt-1">
                      Uploaded: <span className="text-gray-900 font-medium">{formatDate(referral.uploadedAt)}</span>
                    </p>
                  </div>

                  <div className="ml-4 flex flex-col gap-1 shrink-0">
                    <button
                      onClick={() => handleViewReferral(referral)}
                      className="px-3 py-1.5 bg-blue-600 text-white rounded-md text-xs hover:bg-blue-700"
                    >
                      <CheckCircle className="w-3 h-3 inline-block mr-1" /> Review
                    </button>
                    <button
                      onClick={() => handleDownloadDocument(referral.id)}
                      className="px-3 py-1.5 bg-gray-600 text-white rounded-md text-xs hover:bg-gray-700"
                    >
                      <Download className="w-3 h-3 inline-block mr-1" /> Download
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {selectedReferral && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-base font-semibold text-gray-900">
                  Review Referral - {selectedReferral.patient.name}
                </h2>
                <button
                  onClick={() => setSelectedReferral(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="w-5 h-5" />
                </button>
              </div>

              <div className="grid grid-cols-1 gap-2 mb-4">
                <div>
                  <p className="text-xs text-gray-500">Name</p>
                  <p className="text-sm font-medium">{selectedReferral.patient.name}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Email</p>
                  <p className="text-sm font-medium">{selectedReferral.patient.email}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Uploaded</p>
                  <p className="text-sm font-medium">{formatDate(selectedReferral.uploadedAt)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Referring GP</p>
                  <p className="text-sm font-medium">{selectedReferral.gpName}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Provider Number</p>
                  <p className="text-sm font-medium">{selectedReferral.gpProviderNumber}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Referral Date</p>
                  <p className="text-sm font-medium">{formatDate(selectedReferral.referralDate)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Expires</p>
                  <p className="text-sm font-medium">{formatDate(selectedReferral.expiryDate)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Document</p>
                  <div className="flex items-center gap-1">
                    <p className="text-sm font-medium">{selectedReferral.documentFileName}</p>
                    <button
                      onClick={() => handleDownloadDocument(selectedReferral.id)}
                      className="text-blue-600 hover:text-blue-800 text-xs flex items-center gap-1"
                    >
                      <Download className="w-3 h-3" /> Download
                    </button>
                  </div>
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  {isRejecting ? 'Rejection Reason *' : 'Verification Notes (optional)'}
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  required={isRejecting}
                  className="w-full px-2 py-1 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm"
                  placeholder={isRejecting ? 'Please provide a reason for rejection...' : 'Any notes (optional)...'}
                />
              </div>

              <div className="flex gap-3">
                {!isRejecting && (
                  <button
                    onClick={() => handleVerifyOrReject(false)}
                    disabled={verifying}
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {verifying ? 'Verifying...' : 'Verify'}
                  </button>
                )}
                {!isRejecting && (
                  <button
                    onClick={() => setIsRejecting(true)}
                    className="flex-1 bg-red-600 hover:bg-red-700 text-white py-2 px-4 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                  >
                    Reject
                  </button>
                )}
                {isRejecting && (
                  <button
                    onClick={handleVerifyOrReject}
                    disabled={verifying || !notes.trim()}
                    className="w-full bg-red-600 hover:bg-red-700 text-white py-2 px-4 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {verifying ? 'Rejecting...' : 'Confirm Rejection'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReferralVerification;
